import { type ReactNode } from 'react'
import {
  User, Award, CheckCircle, FileText, TrendingUp,
  BarChart3, Medal, BookOpen, Star,
} from 'lucide-react'
import { useLang } from '../i18n/LangContext'

// ---- Types ----
type Status = 'published' | 'reviewing' | 'draft'
type Grade = 'A+' | 'A' | 'B+' | 'B' | null

interface Contribution {
  id: string
  title: { zh: string; en: string }
  difficulty: string
  status: Status
  evaluations: number
  passRate: number | null // null when no evaluations yet
  grade: Grade
  attribution: { zh: string; en: string }
}

interface ModelPerf {
  id: string
  model: string
  avgScore: number
  passRate: number
  tone: 'green' | 'blue' | 'yellow' | 'red'
}

// ---- Status badge config ----
const statusConfig: Record<Status, { bg: string; text: string }> = {
  published: { bg: 'rgba(74,222,128,0.12)', text: 'var(--color-success)' },
  reviewing: { bg: 'rgba(251,191,36,0.12)', text: 'var(--color-warning)' },
  draft:     { bg: 'rgba(90,94,120,0.18)', text: 'var(--color-text-muted)' },
}

// ---- Quality grade config ----
const gradeConfig: Record<Exclude<Grade, null>, { color: string; bg: string }> = {
  'A+': { color: '#FFD700', bg: 'rgba(255,215,0,0.12)' },
  'A':  { color: 'var(--color-success)', bg: 'rgba(74,222,128,0.12)' },
  'B+': { color: 'var(--color-info)', bg: 'rgba(96,165,250,0.12)' },
  'B':  { color: 'var(--color-text-muted)', bg: 'rgba(90,94,120,0.18)' },
}

// ---- Tone color mapping for model performance ----
const toneColor: Record<ModelPerf['tone'], string> = {
  green: 'var(--color-success)',
  blue: 'var(--color-info)',
  yellow: 'var(--color-warning)',
  red: 'var(--color-danger)',
}

// ---- Mock data ----
const expert = {
  name: { zh: '张教授', en: 'Prof. Zhang' },
  role: { zh: '旅游规划专家', en: 'Tourism Planning Expert' },
  organization: { zh: '浙江旅游职业学院', en: 'Zhejiang Tourism College' },
  badge: { zh: '认证专家', en: 'Verified Expert' },
}

const profileStats = [
  { label: { zh: '已贡献任务', en: 'Contributions' }, value: 12 },
  { label: { zh: '通过审核', en: 'Approved' }, value: 9 },
  { label: { zh: '评测次数', en: 'Evaluations' }, value: 48 },
  { label: { zh: '平均通过率', en: 'Avg Pass Rate' }, value: '62%' },
]

const statCards = [
  {
    id: 'total',
    label: { zh: '已贡献任务', en: 'Total Contributions' },
    value: '12',
    icon: TrendingUp,
  },
  {
    id: 'approved',
    label: { zh: '通过审核', en: 'Approved' },
    value: '9 (75%)',
    icon: CheckCircle,
  },
  {
    id: 'evals',
    label: { zh: '总评测次数', en: 'Total Evaluations' },
    value: '48',
    icon: BarChart3,
  },
  {
    id: 'passRate',
    label: { zh: '平均通过率', en: 'Avg Pass Rate' },
    value: '62%',
    icon: TrendingUp,
  },
]

const contributions: Contribution[] = [
  {
    id: 'c1',
    title: { zh: '杭州3日亲子游', en: '3-Day Hangzhou Family Trip' },
    difficulty: 'L4',
    status: 'published',
    evaluations: 12,
    passRate: 58,
    grade: 'A',
    attribution: { zh: '已署名', en: 'Attributed' },
  },
  {
    id: 'c2',
    title: { zh: '商务杭州2日游', en: '2-Day Hangzhou Business Trip' },
    difficulty: 'L3',
    status: 'published',
    evaluations: 8,
    passRate: 75,
    grade: 'A',
    attribution: { zh: '已署名', en: 'Attributed' },
  },
  {
    id: 'c3',
    title: { zh: '亲子异常天气处理', en: 'Family Trip Adverse Weather Handling' },
    difficulty: 'L7',
    status: 'reviewing',
    evaluations: 0,
    passRate: null,
    grade: null,
    attribution: { zh: '待定', en: 'Pending' },
  },
  {
    id: 'c4',
    title: { zh: '新能源市场进入策略', en: 'EV Market Entry Strategy' },
    difficulty: 'L6',
    status: 'published',
    evaluations: 15,
    passRate: 40,
    grade: 'A+',
    attribution: { zh: '已署名', en: 'Attributed' },
  },
  {
    id: 'c5',
    title: { zh: '家庭综合财务规划', en: 'Comprehensive Family Financial Plan' },
    difficulty: 'L5',
    status: 'published',
    evaluations: 13,
    passRate: 69,
    grade: 'B+',
    attribution: { zh: '已署名', en: 'Attributed' },
  },
]

const modelPerfs: ModelPerf[] = [
  { id: 'm1', model: 'GPT-4o',     avgScore: 72, passRate: 75, tone: 'green' },
  { id: 'm2', model: 'Claude 3.5', avgScore: 68, passRate: 62, tone: 'blue' },
  { id: 'm3', model: 'DeepSeek V3', avgScore: 65, passRate: 58, tone: 'yellow' },
  { id: 'm4', model: 'Gemini 2.0',  avgScore: 58, passRate: 42, tone: 'red' },
]

export default function ExpertDashboardPage() {
  const { lang } = useLang()

  // ---- Helpers ----
  function statusLabel(s: Status): string {
    switch (s) {
      case 'published': return lang === 'zh' ? '已发布' : 'Published'
      case 'reviewing': return lang === 'zh' ? '审核中' : 'Reviewing'
      case 'draft':     return lang === 'zh' ? '草稿' : 'Draft'
    }
  }

  function difficultyColor(d: string): string {
    const level = parseInt(d.replace('L', ''), 10)
    if (level >= 6) return 'var(--color-danger)'
    if (level >= 4) return 'var(--color-warning)'
    return 'var(--color-success)'
  }

  // ---- Section heading ----
  function SectionHeading({ icon: Icon, title }: { icon: typeof User; title: string }) {
    return (
      <div className="flex items-center gap-2 mb-5">
        <Icon size={18} style={{ color: 'var(--color-accent)' }} />
        <h2 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>{title}</h2>
      </div>
    )
  }

  return (
    <div className="container-page px-4 sm:px-6 lg:px-8 xl:px-12 py-8">
      {/* ============ 1. Header ============ */}
      <div className="flex items-center gap-3 mb-6">
        <div
          className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
          style={{ backgroundColor: 'var(--color-accent-glow)' }}
        >
          <User size={22} style={{ color: 'var(--color-accent)' }} />
        </div>
        <div>
          <h1 className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
            {lang === 'zh' ? '专家工作台' : 'Expert Dashboard'}
          </h1>
          <p className="text-sm mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
            {lang === 'zh' ? '管理您的贡献，追踪评测影响' : 'Manage your contributions and track evaluation impact'}
          </p>
        </div>
      </div>

      {/* ============ 2. Expert Profile Card ============ */}
      <section className="mb-8">
        <div
          className="rounded-xl border p-6"
          style={{
            backgroundColor: 'var(--color-bg-card)',
            borderColor: 'var(--color-border)',
          }}
        >
          <div className="flex flex-col lg:flex-row lg:items-center gap-6">
            {/* Avatar + identity */}
            <div className="flex items-center gap-4 lg:w-1/2">
              <div
                className="w-16 h-16 rounded-full flex items-center justify-center shrink-0"
                style={{ backgroundColor: 'var(--color-accent-glow)', border: '1px solid var(--color-accent)' }}
              >
                <User size={28} style={{ color: 'var(--color-accent)' }} />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                    {expert.name[lang]}
                  </h2>
                  <span
                    className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor: 'rgba(74,222,128,0.12)',
                      color: 'var(--color-success)',
                      border: '1px solid rgba(74,222,128,0.3)',
                    }}
                  >
                    <CheckCircle size={11} />
                    {expert.badge[lang]}
                  </span>
                </div>
                <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                  {expert.role[lang]}
                </p>
                <p className="text-xs mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                  {expert.organization[lang]}
                </p>
              </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 lg:w-1/2">
              {profileStats.map(stat => (
                <div
                  key={stat.label[lang]}
                  className="rounded-lg p-3 text-center"
                  style={{ backgroundColor: 'var(--color-bg-tertiary)', border: '1px solid var(--color-border)' }}
                >
                  <div className="text-xl font-bold" style={{ color: 'var(--color-accent)' }}>
                    {stat.value}
                  </div>
                  <div className="text-[11px] mt-0.5" style={{ color: 'var(--color-text-muted)' }}>
                    {stat.label[lang]}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============ 3. Contribution Stats (4 cards) ============ */}
      <section className="mb-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {statCards.map(({ id, label, value, icon: Icon }) => (
            <div
              key={id}
              className="rounded-xl border p-5 flex items-center gap-4"
              style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
            >
              <div
                className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0"
                style={{ backgroundColor: 'var(--color-accent-glow)' }}
              >
                <Icon size={20} style={{ color: 'var(--color-accent)' }} />
              </div>
              <div className="min-w-0">
                <div className="text-xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{value}</div>
                <div className="text-xs mt-0.5" style={{ color: 'var(--color-text-secondary)' }}>
                  {label[lang]}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============ 4. My Contributions Table ============ */}
      <section className="mb-8">
        <SectionHeading
          icon={FileText}
          title={lang === 'zh' ? '我的贡献' : 'My Contributions'}
        />
        <div
          className="rounded-xl overflow-hidden"
          style={{ backgroundColor: 'var(--color-bg-card)', border: '1px solid var(--color-border)' }}
        >
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                    {lang === 'zh' ? '任务标题' : 'Task Title'}
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                    {lang === 'zh' ? '难度' : 'Difficulty'}
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                    {lang === 'zh' ? '状态' : 'Status'}
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                    {lang === 'zh' ? '评测次数' : 'Evaluations'}
                  </th>
                  <th className="text-right px-4 py-3 text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                    {lang === 'zh' ? '通过率' : 'Pass Rate'}
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                    {lang === 'zh' ? '质量等级' : 'Quality'}
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                    {lang === 'zh' ? '署名' : 'Attribution'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {contributions.map(c => {
                  const status = statusConfig[c.status]
                  const grade = c.grade ? gradeConfig[c.grade] : null
                  return (
                    <tr
                      key={c.id}
                      className="transition-colors hover:opacity-80"
                      style={{ borderBottom: '1px solid var(--color-border)' }}
                    >
                      {/* Title */}
                      <td className="px-4 py-3">
                        <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                          {c.title[lang]}
                        </span>
                      </td>
                      {/* Difficulty */}
                      <td className="px-4 py-3">
                        <span
                          className="text-xs font-mono font-semibold px-2 py-0.5 rounded"
                          style={{
                            color: difficultyColor(c.difficulty),
                            backgroundColor: 'var(--color-bg-tertiary)',
                          }}
                        >
                          {c.difficulty}
                        </span>
                      </td>
                      {/* Status */}
                      <td className="px-4 py-3">
                        <span
                          className="text-[11px] font-medium px-2 py-0.5 rounded-full"
                          style={{ backgroundColor: status.bg, color: status.text }}
                        >
                          {statusLabel(c.status)}
                        </span>
                      </td>
                      {/* Evaluations */}
                      <td className="px-4 py-3 text-right">
                        <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>{c.evaluations}</span>
                      </td>
                      {/* Pass Rate */}
                      <td className="px-4 py-3 text-right">
                        {c.passRate === null ? (
                          <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                        ) : (
                          <span className="text-sm font-mono" style={{ color: 'var(--color-text-secondary)' }}>
                            {c.passRate}%
                          </span>
                        )}
                      </td>
                      {/* Quality grade */}
                      <td className="px-4 py-3">
                        {grade && c.grade ? (
                          <span
                            className="text-xs font-bold px-2 py-0.5 rounded"
                            style={{ color: grade.color, backgroundColor: grade.bg }}
                          >
                            {c.grade}
                          </span>
                        ) : (
                          <span style={{ color: 'var(--color-text-muted)' }}>—</span>
                        )}
                      </td>
                      {/* Attribution */}
                      <td className="px-4 py-3">
                        <span className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
                          {c.attribution[lang]}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* ============ 5. Model Performance on My Tasks ============ */}
      <section className="mb-8">
        <SectionHeading
          icon={BarChart3}
          title={lang === 'zh' ? '模型在我的任务上的表现' : 'Model Performance on My Tasks'}
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {modelPerfs.map(m => {
            const color = toneColor[m.tone]
            return (
              <div
                key={m.id}
                className="rounded-xl border p-5"
                style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
              >
                <div className="flex items-center justify-between mb-4">
                  <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                    {m.model}
                  </span>
                  <span
                    className="w-2.5 h-2.5 rounded-full"
                    style={{ backgroundColor: color }}
                    title={m.tone}
                  />
                </div>

                {/* Avg score */}
                <div className="mb-3">
                  <div className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
                    {lang === 'zh' ? '平均分' : 'Avg Score'}
                  </div>
                  <div className="text-2xl font-bold font-mono" style={{ color: 'var(--color-text-primary)' }}>
                    {m.avgScore}
                  </div>
                </div>

                {/* Pass rate with progress bar */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-[11px]" style={{ color: 'var(--color-text-muted)' }}>
                      {lang === 'zh' ? '通过率' : 'Pass Rate'}
                    </span>
                    <span className="text-xs font-mono" style={{ color }}>
                      {m.passRate}%
                    </span>
                  </div>
                  <div
                    className="w-full h-2 rounded-full overflow-hidden"
                    style={{ backgroundColor: 'var(--color-bg-tertiary)' }}
                  >
                    <div
                      className="h-full rounded-full transition-all"
                      style={{ width: `${m.passRate}%`, backgroundColor: color }}
                    />
                  </div>
                </div>
              </div>
            )
          })}
        </div>
      </section>

      {/* ============ 6. Rewards & Recognition ============ */}
      <section>
        <SectionHeading
          icon={Award}
          title={lang === 'zh' ? '奖励与认可' : 'Rewards & Recognition'}
        />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Attributed Paper */}
          <div
            className="rounded-xl border p-5"
            style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'var(--color-accent-glow)' }}
              >
                <BookOpen size={18} style={{ color: 'var(--color-accent)' }} />
              </div>
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                {lang === 'zh' ? '署名论文' : 'Attributed Paper'}
              </span>
            </div>
            <p className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
              DecisionCraft Benchmark Technical Report (2026)
            </p>
            <div className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full"
              style={{ backgroundColor: 'rgba(74,222,128,0.12)', color: 'var(--color-success)' }}
            >
              <CheckCircle size={11} />
              {lang === 'zh' ? '已确认' : 'Confirmed'}
            </div>
          </div>

          {/* Bonus Record */}
          <div
            className="rounded-xl border p-5"
            style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'var(--color-accent-glow)' }}
              >
                <Medal size={18} style={{ color: 'var(--color-accent)' }} />
              </div>
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                {lang === 'zh' ? '奖金记录' : 'Bonus Record'}
              </span>
            </div>
            <p className="text-2xl font-bold font-mono mb-1" style={{ color: '#FFD700' }}>
              ¥2,500
            </p>
            <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              {lang === 'zh' ? '3个A级任务' : '3 A-grade tasks'}
            </p>
          </div>

          {/* Expert Ranking */}
          <div
            className="rounded-xl border p-5"
            style={{ backgroundColor: 'var(--color-bg-card)', borderColor: 'var(--color-border)' }}
          >
            <div className="flex items-center gap-2 mb-3">
              <div
                className="w-9 h-9 rounded-lg flex items-center justify-center"
                style={{ backgroundColor: 'var(--color-accent-glow)' }}
              >
                <Star size={18} style={{ color: 'var(--color-accent)' }} />
              </div>
              <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: 'var(--color-text-muted)' }}>
                {lang === 'zh' ? '专家排名' : 'Expert Ranking'}
              </span>
            </div>
            <p className="text-2xl font-bold font-mono mb-1" style={{ color: 'var(--color-accent)' }}>
              #3
            </p>
            <p className="text-xs" style={{ color: 'var(--color-text-secondary)' }}>
              {lang === 'zh' ? '旅游领域' : 'Tourism domain'}
            </p>
          </div>
        </div>
      </section>
    </div>
  )
}
